import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  CssBaseline,
  GlobalStyles,
  ThemeProvider,
  createTheme,
  type PaletteMode,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ColorModeContext, type ColorModeContextValue } from '@/theme/colorMode';

const STORAGE_KEY = 'nangbook-theme-mode';
const brandColors = {
  primaryNavy: '#071F49',
  accentGold: '#D99A12',
  darkSlate: '#1F2933',
  lightBackground: '#FFFFFF',
  offWhiteBackground: '#F8FAFC',
  mutedBorder: '#CBD5E1',
};

export function AppThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<PaletteMode>(() => getInitialMode());

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  const colorMode = useMemo<ColorModeContextValue>(
    () => ({
      mode,
      toggleMode: () => setMode((current) => (current === 'light' ? 'dark' : 'light')),
    }),
    [mode]
  );

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: mode === 'light' ? brandColors.primaryNavy : brandColors.accentGold,
            contrastText: mode === 'light' ? brandColors.lightBackground : brandColors.primaryNavy,
          },
          secondary: {
            main: brandColors.accentGold,
            contrastText: brandColors.primaryNavy,
          },
          background: {
            default: mode === 'light' ? brandColors.offWhiteBackground : brandColors.darkSlate,
            paper: mode === 'light' ? brandColors.lightBackground : brandColors.primaryNavy,
          },
          text: {
            primary: mode === 'light' ? brandColors.darkSlate : brandColors.lightBackground,
            secondary: mode === 'light' ? alpha(brandColors.darkSlate, 0.72) : brandColors.mutedBorder,
          },
          divider:
            mode === 'light' ? brandColors.mutedBorder : alpha(brandColors.mutedBorder, 0.22),
          common: {
            white: brandColors.lightBackground,
          },
          success: {
            main: '#2e7d32',
          },
          warning: {
            main: '#ed6c02',
          },
        },
        shape: {
          borderRadius: 8,
        },
        typography: {
          fontFamily:
            'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          h1: { fontSize: '2rem', fontWeight: 700 },
          h2: { fontSize: '1.5rem', fontWeight: 700 },
          h3: { fontSize: '1.25rem', fontWeight: 700 },
          button: { textTransform: 'none', fontWeight: 700 },
        },
        components: {
          MuiButton: {
            defaultProps: {
              disableElevation: true,
              // Default to a compact button everywhere; the marketing/auth
              // hero CTAs opt back up with an explicit size="large".
              size: 'small',
            },
            styleOverrides: {
              root: {
                borderRadius: 8,
              },
              // Tighter than MUI's defaults — the stock large/medium
              // buttons read as chunky in this UI.
              sizeLarge: {
                paddingTop: 7,
                paddingBottom: 7,
                paddingLeft: 16,
                paddingRight: 16,
              },
              sizeMedium: {
                paddingTop: 5,
                paddingBottom: 5,
                paddingLeft: 12,
                paddingRight: 12,
              },
              outlined: {
                borderColor:
                  mode === 'light' ? brandColors.mutedBorder : alpha(brandColors.mutedBorder, 0.42),
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              notchedOutline: {
                borderColor:
                  mode === 'light' ? brandColors.mutedBorder : alpha(brandColors.mutedBorder, 0.34),
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 8,
                borderColor:
                  mode === 'light' ? brandColors.mutedBorder : alpha(brandColors.mutedBorder, 0.2),
              },
            },
          },
        },
      }),
    [mode]
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles
          styles={{
            html: { height: '100%' },
            body: { minHeight: '100%' },
            '#root': { minHeight: '100%' },
            '@media print': {
              '@page': { margin: '14mm' },
              '*, *::before, *::after': {
                boxShadow: 'none !important',
                textShadow: 'none !important',
                printColorAdjust: 'exact',
              },
              'html, body': {
                fontFamily: 'Arial, Helvetica, sans-serif !important',
                fontSize: '10pt !important',
                lineHeight: '1.35 !important',
              },
              body: {
                background: '#fff !important',
                color: '#000 !important',
              },
              'body, body *': {
                color: '#000 !important',
              },
              'a, a:visited': {
                color: '#000 !important',
                textDecoration: 'none !important',
              },
              'h1, h2, h3, h4, h5, h6, .MuiTypography-h1, .MuiTypography-h2, .MuiTypography-h3, .MuiTypography-h4, .MuiTypography-h5, .MuiTypography-h6': {
                color: '#000 !important',
                fontFamily: 'Arial, Helvetica, sans-serif !important',
                fontWeight: '700 !important',
              },
              'p, span, label, caption, .MuiTypography-root, .MuiFormLabel-root, .MuiInputBase-input': {
                fontFamily: 'Arial, Helvetica, sans-serif !important',
              },
              '.MuiTypography-body1, .MuiTypography-body2, .MuiTableCell-root, .MuiInputBase-input': {
                fontWeight: '500 !important',
              },
              'header, nav, aside, [role="navigation"], .MuiAppBar-root, .MuiDrawer-root, .MuiBottomNavigation-root, button, [aria-label="Open navigation menu"]': {
                display: 'none !important',
              },
              'main, #root': {
                display: 'block !important',
                width: '100% !important',
                margin: '0 !important',
                padding: '0 !important',
              },
              '.MuiPaper-root, .MuiCard-root': {
                boxShadow: 'none !important',
                border: '1pt solid #000 !important',
                borderColor: '#000 !important',
                background: '#fff !important',
              },
              '.MuiTableContainer-root': {
                overflow: 'visible !important',
              },
              'table, .MuiTable-root': {
                width: '100% !important',
                borderCollapse: 'collapse !important',
                borderSpacing: '0 !important',
                border: '1.2pt solid #000 !important',
              },
              'thead, .MuiTableHead-root': {
                display: 'table-header-group !important',
              },
              'tfoot, .MuiTableFooter-root': {
                display: 'table-footer-group !important',
              },
              'tr, .MuiTableRow-root': {
                breakInside: 'avoid !important',
                pageBreakInside: 'avoid !important',
              },
              'th, td, .MuiTableCell-root': {
                background: '#fff !important',
                border: '1pt solid #000 !important',
                padding: '5pt 6pt !important',
                color: '#000 !important',
                verticalAlign: 'top !important',
              },
              'tr, .MuiTableRow-root, .MuiTableBody-root': {
                background: '#fff !important',
              },
              'th, .MuiTableCell-head': {
                background: '#fff !important',
                color: '#000 !important',
                fontWeight: '700 !important',
              },
              'caption, .MuiTable-root caption': {
                color: '#000 !important',
                fontWeight: '700 !important',
                textAlign: 'left !important',
                captionSide: 'top !important',
                paddingBottom: '5pt !important',
              },
              '.MuiChip-root': {
                border: '1pt solid #000 !important',
                background: '#fff !important',
                color: '#000 !important',
              },
              '.MuiChip-label': {
                color: '#000 !important',
                fontWeight: '700 !important',
              },
              '.MuiOutlinedInput-root, .MuiInputBase-root': {
                background: '#fff !important',
                color: '#000 !important',
              },
              '.MuiOutlinedInput-notchedOutline, fieldset': {
                borderColor: '#000 !important',
                borderWidth: '1pt !important',
              },
              '.MuiDivider-root': {
                borderColor: '#000 !important',
              },
            },
          }}
        />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

function getInitialMode(): PaletteMode {
  const stored = window.localStorage.getItem(STORAGE_KEY);

  if (stored === 'light' || stored === 'dark') {
    return stored;
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
